import { useAuth } from '../../hooks/useAuth'
import { useFriendships } from '../../hooks/useFriendships'
import { useGuestGuard } from '../../hooks/useGuestGuard'
import { friendInvitePrompt } from '../../lib/guestPrompts'
import { unknownMemberName } from '../../lib/commonStrings'
import { compareByDisplayName } from '../../lib/utils'
import { useTransitionNavigate } from '../../lib/viewTransition'
import { Section } from '../../components/Section'
import { UserRow } from '../../components/UserRow'
import { FriendStatusAction, getFriendStatus } from './FriendStatusAction'
import { MemberAdminMenu } from './MemberAdminMenu'
import { canDemoteAdmin, canRemoveMember, groupAdminIds } from '@shared/groupRoles'
import type { Group, UserProfile } from '../../types'

interface Props {
  group: Group
  memberProfiles: Record<string, UserProfile>
  /** True while some profile may still arrive — a missing one is a skeleton, not "Unknown". */
  profilesLoading?: boolean
  /** Present only when the viewer is an admin: promote/demote from each member's row. */
  onSetAdmin?: (uid: string, makeAdmin: boolean) => void
  onRemoveMember?: (uid: string) => void
}

export function MembersSection({ group, memberProfiles, profilesLoading, onSetAdmin, onRemoveMember }: Props) {
  const { user, signedInUid } = useAuth()
  const { friendIdSet: friendSet, incoming, outgoing, sendRequest, acceptRequest } = useFriendships(signedInUid)
  const { guard } = useGuestGuard()
  const navigate = useTransitionNavigate()
  const outgoingSet = new Set(outgoing.flatMap(f => f.userIds.filter(u => u !== user?.uid)))
  const incomingSet = new Set(incoming.flatMap(f => f.userIds.filter(u => u !== user?.uid)))

  const currentUserId = user?.uid ?? null
  const adminIds = new Set(groupAdminIds(group))
  const ordered = [...group.memberIds].sort((a, b) => {
    if (a === currentUserId) return -1
    if (b === currentUserId) return 1
    if (a === group.createdBy) return -1
    if (b === group.createdBy) return 1
    if (adminIds.has(a) !== adminIds.has(b)) return adminIds.has(a) ? -1 : 1
    return compareByDisplayName(a, b, memberProfiles)
  })

  function renderAction(uid: string, name: string) {
    const isSelf = uid === user?.uid
    const adminMenu = onSetAdmin && onRemoveMember && (
      <MemberAdminMenu
        name={name}
        groupName={group.name}
        isAdmin={adminIds.has(uid)}
        isSelf={isSelf}
        canPromote={uid !== group.createdBy}
        canDemote={canDemoteAdmin(group, currentUserId, uid)}
        canRemove={canRemoveMember(group, currentUserId, uid)}
        onSetAdmin={make => onSetAdmin(uid, make)}
        onRemove={() => onRemoveMember(uid)}
      />
    )
    if (isSelf) return adminMenu || undefined
    const status = getFriendStatus(uid, friendSet, incomingSet, outgoingSet)
    return (
      <div className="flex shrink-0 items-center gap-1">
        <FriendStatusAction
          status={status}
          onAccept={() => acceptRequest(uid)}
          onAdd={guard(() => sendRequest(uid, 'group'), friendInvitePrompt(name))}
        />
        {adminMenu}
      </div>
    )
  }

  return (
    <Section testId="group-members">
      <ul className="flex flex-col gap-2">
        {ordered.map(uid => {
          const profile = memberProfiles[uid]
          if (!profile && profilesLoading) return <UserRow key={uid} loading />
          const name = profile?.displayName?.trim() || unknownMemberName()
          return (
            <UserRow
              key={uid}
              name={name}
              profile={profile}
              isYou={uid === user?.uid}
              isCreator={uid === group.createdBy}
              isAdmin={adminIds.has(uid)}
              action={renderAction(uid, name)}
              onClick={() => navigate(uid === user?.uid ? '/profile' : `/users/${uid}`)}
            />
          )
        })}
      </ul>
    </Section>
  )
}
